import React from "react";
import { CheckCircle2, Info, XCircle, X, Send } from "lucide-react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import {
  toast,
  type ToastContentProps,
  type ToastOptions,
} from "react-toastify";
import { reportErrorToAdmins, type ErrorReportDetails } from "./reportError.js";

type AppToastVariant = "success" | "error" | "info";

interface AppToastPayload {
  title: string;
  message: string;
  variant?: AppToastVariant;
  options?: ToastOptions;
  /** Adds an "Avisar Admins" button that emails the details to the team. */
  report?: ErrorReportDetails;
}

interface AppToastCardProps {
  title: string;
  message: string;
  variant: AppToastVariant;
  closeToast?: ToastContentProps["closeToast"];
  report?: ErrorReportDetails;
}

const VARIANT_STYLES: Record<
  AppToastVariant,
  {
    border: string;
    iconWrap: string;
    iconColor: string;
  }
> = {
  success: {
    border: "border-emerald-300",
    iconWrap: "bg-emerald-200/50",
    iconColor: "text-emerald-600",
  },
  error: {
    border: "border-rose-300",
    iconWrap: "bg-rose-100",
    iconColor: "text-rose-600",
  },
  info: {
    border: "border-sky-300",
    iconWrap: "bg-sky-100",
    iconColor: "text-sky-600",
  },
};

const AppToastCard: React.FC<AppToastCardProps> = ({
  title,
  message,
  variant,
  closeToast,
  report,
}) => {
  const { t } = useTranslation(["common", "errors"]);
  const styles = VARIANT_STYLES[variant];
  const VariantIcon =
    variant === "success" ? CheckCircle2 : variant === "error" ? XCircle : Info;
  const [reportStatus, setReportStatus] = React.useState<"idle" | "sending" | "sent" | "failed">("idle");

  const handleReport = async () => {
    if (!report) return;
    setReportStatus("sending");
    const ok = await reportErrorToAdmins(report);
    setReportStatus(ok ? "sent" : "failed");
  };

  return (
    <div
      className={`w-full rounded-2xl border bg-white/80 shadow-lg px-3.5 py-3 ${styles.border}`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 h-8 w-8 rounded-full flex items-center justify-center ${styles.iconWrap}`}
        >
          <VariantIcon className={`h-5 w-5 ${styles.iconColor}`} />
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate whitespace-nowrap text-sm font-semibold leading-5 text-gray-900">
            {title}
          </p>
          <p className="truncate whitespace-nowrap text-sm leading-5 text-gray-600">
            {message}
          </p>
          {report && (
            <button
              type="button"
              onClick={handleReport}
              disabled={reportStatus === "sending" || reportStatus === "sent"}
              className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-rose-700 underline decoration-dotted underline-offset-2 disabled:no-underline disabled:opacity-70"
            >
              <Send className="h-3 w-3" />
              {reportStatus === "sent"
                ? t("errors:reportAdmins.sent")
                : reportStatus === "sending"
                  ? t("errors:reportAdmins.sending")
                  : reportStatus === "failed"
                    ? t("errors:reportAdmins.failed")
                    : t("errors:reportAdmins.action")}
            </button>
          )}
        </div>

        <span className="rounded-md border border-gray-200 bg-white/80">
          <button
            type="button"
            onClick={() => closeToast?.()}
            className="p-1 text-gray-600 transition-colors hover:text-gray-800"
            aria-label={t("toast.closeNotification")}
          >
            <X className="h-4 w-4" />
          </button>
        </span>
      </div>
    </div>
  );
};

const BASE_TOAST_OPTIONS: ToastOptions = {
  position: "top-center",
  autoClose: 3000,
  hideProgressBar: true,
  closeButton: false,
  className:
    "!mt-3 !mx-auto !w-[360px] md:!max-w-[680px] md:!w-[600px] !min-h-0 !bg-transparent !p-0 !shadow-none",
};

// Maximum toasts visible at once. When a new toast is triggered while this many
// are already on screen, the OLDEST is dismissed immediately (evicted) rather
// than queued — the newest toast always shows. The ToastContainer's `limit`
// prop is intentionally NOT used, because it queues excess toasts instead of
// replacing the oldest.
const MAX_VISIBLE_TOASTS = 2;

// Oldest-first list of currently visible toast ids.
const activeToastIds: Array<string | number> = [];
let toastCounter = 0;

export function showToast({
  title,
  message,
  variant = "success",
  options,
  report,
}: AppToastPayload) {
  // Evict the oldest toast(s) so the new one always fits within the cap.
  while (activeToastIds.length >= MAX_VISIBLE_TOASTS) {
    const oldest = activeToastIds.shift();
    if (oldest !== undefined) toast.dismiss(oldest);
  }

  const toastId = options?.toastId ?? `app-toast-${++toastCounter}`;

  toast(
    ({ closeToast }) => (
      <AppToastCard
        title={title}
        message={message}
        variant={variant}
        closeToast={closeToast}
        report={report}
      />
    ),
    {
      ...BASE_TOAST_OPTIONS,
      // Reported errors stay up until dismissed, so there's time to click "Avisar Admins".
      ...(report ? { autoClose: false as const } : {}),
      ...options,
      toastId,
      onClose: (props) => {
        const index = activeToastIds.indexOf(toastId);
        if (index !== -1) activeToastIds.splice(index, 1);
        options?.onClose?.(props);
      },
    },
  );

  activeToastIds.push(toastId);
  return toastId;
}

export function showSuccessToast(message: string, title = i18n.t("common:toast.successTitle")) {
  return showToast({
    variant: "success",
    title,
    message,
  });
}

export function showInfoToast(message: string, title = i18n.t("common:toast.infoTitle")) {
  return showToast({
    variant: "info",
    title,
    message,
  });
}

export function showErrorToast(
  message: string,
  title = i18n.t("common:toast.errorTitle"),
  report?: ErrorReportDetails,
) {
  return showToast({
    variant: "error",
    title,
    message,
    report,
  });
}

export function showMatchToast(animalName: string) {
  return showToast({
    variant: "success",
    title: i18n.t("common:toast.matchTitle"),
    message: i18n.t("common:toast.matchMessage", { animalName }),
  });
}
